import React from 'react';
import { Heading } from '../../../../_lib/components/Heading/Heading';
import { DateParam, Fieldset, Input, RequiredHelpPhrase, Row, TextParam } from '../../../../_lib/Form2';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { formParamsUtils } from '../../../../_lib/Form2/utils/formParamsUtils';

export type AccessLevelInformationParamType = ReturnType<
  typeof formParamsUtils<
    | DateParam<'mandateEnd'>
    | TextParam<'mandateName'>
    | TextParam<'mandateAdministration'>
    | TextParam<'mandateContactPersonEmail'>
    | TextParam<'officeName'>
    | TextParam<'officeOrgunitHeadEmail'>
    | TextParam<'officeStreet'>
    | TextParam<'officePostcode'>
    | TextParam<'officeLocation'>
  >
>;

type AccessLevelInformationFormType = {
  accessLevel?: string;
  params: AccessLevelInformationParamType;
};

export function AccessLevelInformationForm({ accessLevel, params }: AccessLevelInformationFormType) {
  const { t } = useSonTranslation('AccessLevel');

  if (!params.anyParameters()) {
    return null;
  }

  const mandateEndParam = params.findByName('mandateEnd');
  const mandateNameParam = params.findByName('mandateName');
  const mandateAdministrationParam = params.findByName('mandateAdministration');
  const mandateContactPersonEmailParam = params.findByName('mandateContactPersonEmail');
  const officeNameParam = params.findByName('officeName');
  const officeOrgunitHeadEmailParam = params.findByName('officeOrgunitHeadEmail');
  const officeStreetParam = params.findByName('officeStreet');
  const officePostcodeParam = params.findByName('officePostcode');
  const officeLocationParam = params.findByName('officeLocation');

  const showMandateFieldSet =
    !!mandateEndParam || !!mandateNameParam || !!mandateAdministrationParam || !!mandateContactPersonEmailParam;

  return (
    <div className="access-level__information-form-container">
      <Heading className="access-level__choices-form-heading" hierarchy={3}>
        {/* @ts-expect-error -- known issue from translation typing */}
        {t('form.information.headline', { accessLevel: t(`AccessLevel:form.accessLevels.${accessLevel}.title`) })}
      </Heading>
      <RequiredHelpPhrase />
      {showMandateFieldSet && (
        <Fieldset legend={t('form.information.fieldSets.mandate')}>
          {mandateNameParam && (
            <Row>
              <Input label={t('form.information.mandateName')} param={mandateNameParam} />
            </Row>
          )}
          {mandateEndParam && (
            <Row>
              <Input label={t('form.information.mandateEnd')} param={mandateEndParam} />
            </Row>
          )}
          {mandateAdministrationParam && (
            <Row>
              <Input label={t('form.information.mandateAdministration')} param={mandateAdministrationParam} />
            </Row>
          )}
          {mandateContactPersonEmailParam && (
            <Row>
              <Input
                type="email"
                label={t('form.information.mandateContactPersonEmail')}
                param={mandateContactPersonEmailParam}
              />
            </Row>
          )}
        </Fieldset>
      )}
      <Fieldset legend={t('form.information.fieldSets.office')}>
        {officeStreetParam && (
          <Row>
            {officeStreetParam && <Input label={t('form.information.officeStreet')} param={officeStreetParam} />}
          </Row>
        )}
        {(officePostcodeParam || officeLocationParam) && (
          <Row>
            {officePostcodeParam && (
              <div className="access-level__information-form-post-code">
                <Input label={t('form.information.officePostcode')} param={officePostcodeParam} />
              </div>
            )}
            {officeLocationParam && (
              <div className="access-level__information-form-location">
                <Input label={t('form.information.officeLocation')} param={officeLocationParam} />
              </div>
            )}
          </Row>
        )}
        {officeNameParam && (
          <Row>
            <Input label={t('form.information.officeName')} param={officeNameParam} />
          </Row>
        )}
        {officeOrgunitHeadEmailParam && (
          <Row>
            <Input
              type="email"
              label={t('form.information.officeOrgunitHeadEmail')}
              param={officeOrgunitHeadEmailParam}
            />
          </Row>
        )}
      </Fieldset>
    </div>
  );
}
